import { useEffect, useState } from "react";
import {
  ApolloCache,
  useApolloClient,
  useMutation,
  useQuery,
} from "@apollo/client";
import {
  CoinType,
  Inventory,
  InventoryAvatarData,
  InventoryCost,
} from "@onmo/graphql/graphql";
import {
  applyInventoryGQL,
  getListInventoriesGQL,
  purchaseInventoryGQL,
} from "../graphql";
import { Query } from "@onmo/graphql/legacyGraphql";
import { uniqBy } from "lodash-es";

export const getInventoryCost = (cost?: InventoryCost[]) => {
  const spendableGzTokens = cost?.find(
    (c) => c.coinType === CoinType.SpendableGzTokens,
  );

  if (!spendableGzTokens) return undefined;
  return {
    coinType: spendableGzTokens.coinType,
    amount: spendableGzTokens.amount,
  };
};

export interface IAvatarBundle {
  id: string;
  title: string;
  metadata?: Inventory["metadata"];
  avatars: Inventory[];
}

const isAvatar = (item: Inventory) =>
  item.data?.__typename === "InventoryAvatarData";

// Free avatars are the items of every free bundle
export const useFetchFreeAvatars = (skip = false) => {
  const client = useApolloClient();
  const bundles = useQuery<Query>(getListInventoriesGQL, {
    skip,
    variables: {
      input: {
        filters: { restrictToFree: true, restrictToBundles: true },
      },
    },
  });
  const bundleList = bundles.data?.inventories?.items ?? [];
  const bundleIds = bundleList.map((bundle) => bundle.id);
  const [avatarBundles, setAvatarBundles] = useState<IAvatarBundle[]>([]);
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  const [itemsError, setItemsError] = useState<unknown>();

  useEffect(() => {
    if (skip || !bundleIds.length) return;
    let isCancelled = false;
    setIsLoadingItems(true);
    Promise.all(
      bundleIds.map((bundleInventoryId) =>
        client.query<Query>({
          query: getListInventoriesGQL,
          variables: { input: { filters: { bundleInventoryId } } },
        }),
      ),
    )
      .then((results) => {
        if (isCancelled) return;
        setAvatarBundles(
          bundleList.map((bundle, index) => {
            const items = (results[index].data?.inventories?.items ??
              []) as unknown as Inventory[];
            return {
              id: bundle.id,
              title: bundle.title,
              metadata: bundle.metadata,
              avatars: items.filter(isAvatar),
            };
          }),
        );
      })
      .catch((error) => !isCancelled && setItemsError(error))
      .finally(() => !isCancelled && setIsLoadingItems(false));
    return () => {
      isCancelled = true;
    };
  }, [skip, bundleIds.join(",")]);

  const freeAvatars = uniqBy(
    avatarBundles.flatMap((bundle) => bundle.avatars),
    "id",
  );

  return {
    avatarBundles,
    freeAvatars,
    total: freeAvatars.length,
    loading: bundles.loading || isLoadingItems,
    error: bundles.error || itemsError,
  };
};

export const usePurchaseInventory = () => {
  const [purchaseInventoryAction] = useMutation(purchaseInventoryGQL);

  //purchase avatar/bundle function
  const purchaseInventory = async (inventory: Inventory) => {
    try {
      const result = await purchaseInventoryAction({
        variables: {
          input: {
            applyImmediately: inventory.applyImmediately,
            inventoryId: inventory.id,
            inventoryCost: getInventoryCost(
              inventory?.costs as InventoryCost[],
            ),
          },
        },
      });

      return result;
    } catch (error) {
      console.error(error);
    }
  };

  return purchaseInventory;
};

// The avatar goes to the profile the x-profile-token belongs to
export const useApplyAvatar = () => {
  const [purchaseInventoryAction] = useMutation(purchaseInventoryGQL);
  const [applyInventoryAction] = useMutation(applyInventoryGQL);

  return (avatar: Inventory, profileId: string, profileToken?: string) => {
    const context = profileToken ? { profileToken } : undefined;
    const setProfileAvatar = (cache: ApolloCache<unknown>) => {
      cache.modify({
        id: cache.identify({ __typename: "Profile", id: profileId }),
        fields: { avatar: () => (avatar.data as InventoryAvatarData)?.url },
      });
    };
    if (avatar.isPurchased) {
      return applyInventoryAction({
        variables: { input: { inventoryId: avatar.id } },
        context,
        update: setProfileAvatar,
      });
    }
    return purchaseInventoryAction({
      variables: {
        input: {
          applyImmediately: true,
          inventoryId: avatar.id,
          inventoryCost: getInventoryCost(avatar.costs as InventoryCost[]),
        },
      },
      context,
      update: (cache) => {
        setProfileAvatar(cache);
        cache.modify({
          id: cache.identify(avatar),
          fields: { isPurchased: () => true },
        });
      },
    });
  };
};
